import { ACTION_ICONS } from "../../config/siteConfig";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { Modal } from "react-bootstrap";
import DetailView from "../location/DetailView";
import { useLoadingBar } from "react-top-loading-bar";
import { CSVLink } from "react-csv";
import toast from "react-hot-toast";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import ContentLoader from "../loader/ContentLoader";
import { staffService } from "../../services/staff.service";
import { rbacRolesService } from "../../services/rbac-roles.service";
import { SOMETHING_WENT_WRONG } from "../../constants/strings";

const STATUS_FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: 8,
    border: "1px solid #e8edf5",
    background: "#f8f9fe",
    minHeight: 34,
    height: 34,
    fontSize: 13,
    boxShadow: "none",
    "&:hover": { borderColor: state.isFocused ? "var(--btn-bg)" : "#e8edf5" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const FORM_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const labelStyle = { fontWeight: 600, fontSize: 12.5, color: "#334155", marginBottom: 5 };
const inputStyle = { borderRadius: 6, border: "1px solid #c3cad6", fontSize: 12, height: 34, background: "#fff" };
const formSelectStyles = {
  ...selectStyles,
  control: (base) => ({
    ...base,
    borderRadius: 6,
    border: "1px solid #c3cad6",
    minHeight: 34,
    height: 34,
    fontSize: 12,
    boxShadow: "none",
    "&:hover": { borderColor: "var(--btn-bg)" },
  }),
};

const initialForm = {
  name: "",
  email: "",
  phone: "",
  password: "",
  role_code: "",
  status: "active",
};

const initialPasswordForm = {
  newPassword: "",
  confirmNewPassword: "",
};

const StaffList = () => {
  const { start, complete } = useLoadingBar();

  const [loading, setLoading] = useState(false);
  const [staffRes, setStaffRes] = useState({ pagination: {}, staff: [] });

  const [pageValue, setPageValue] = useState(1);
  const [pagingLimit, setPagingLimit] = useState("10");
  const [goPage, setGoPage] = useState("");

  const initialFilters = { search: "", role_code: "", status: "" };
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  const [roleOptions, setRoleOptions] = useState([]);

  const [modal, setModal] = useState({ show: false, mode: "add", id: null });
  const [form, setForm] = useState(initialForm);
  const [modalLoading, setModalLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pwdModal, setPwdModal] = useState({
    show: false,
    name: "",
    userCode: "",
  });
  const [pwdForm, setPwdForm] = useState(initialPasswordForm);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [showPwd, setShowPwd] = useState({});
  const isView = modal.mode === "view";
  const isEdit = modal.mode === "edit";

  useEffect(() => {
    loadStaff();
  }, [pagingLimit, pageValue, appliedFilters]);

  useEffect(() => {
    loadRoleOptions();
  }, []);

  const loadRoleOptions = async () => {
    try {
      const res = await rbacRolesService.list({ status: "active" });
      // agent (1004) and supplier (1003) are not staff roles
      const EXCLUDED_ROLE_CODES = [1003, 1004];
      setRoleOptions(
        (res?.resData?.data || [])
          .filter((r) => !EXCLUDED_ROLE_CODES.includes(Number(r.code)))
          .map((r) => ({
            value: r.code,
            // label: `${r.role_type} (${r.code})`,
            label: `${r.role_type}`,
          })),
      );
    } catch (err) {
      setRoleOptions([]);
    }
  };

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon });
  };

  const loadStaff = async () => {
    try {
      setLoading(true);
      start();
      const res = await staffService.list({
        page: Number(pageValue),
        limit: Number(pagingLimit),
        ...appliedFilters,
      });
      if (res?.success) {
        setStaffRes({
          pagination: res.resData?.pagination || {},
          staff: res.resData?.data || [],
        });
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      complete();
    }
  };

  const handleStatusChange = async (member, newStatus) => {
    if (newStatus === member?.status) return;
    const result = await withReactContent(Swal).fire({
      title: "Change status?",
      text: `Are you sure you want to mark ${member?.name} as ${newStatus}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, change it",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await staffService.updateStatus({
        user_id: member?.id,
        role_code: member?.role_code,
        status: newStatus,
      });
      if (res?.success) {
        toast.success(res.message);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      loadStaff();
    }
  };

  const handleDeleteStaff = async (member) => {
    const result = await withReactContent(Swal).fire({
      title: "Delete staff?",
      text: `Are you sure you want to delete ${member?.name}? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d93025",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await staffService.delete(member?.id);
      if (res?.success) {
        toast.success(res.message);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      loadStaff();
    }
  };

  const closeModal = () => setModal({ ...modal, show: false });

  const openAdd = () => {
    setForm(initialForm);
    setModal({ show: true, mode: "add", id: null });
  };

  const openExisting = async (member, mode) => {
    setModal({ show: true, mode, id: member?.id });
    setForm(initialForm);
    try {
      setModalLoading(true);
      const res = await staffService.details(member?.id, member?.role_code);
      if (!res?.success) {
        toast.error(res?.message);
        setModal((m) => ({ ...m, show: false }));
        return;
      }
      const raw = res.resData?.user ?? res.resData?.data ?? res.resData;
      const d = Array.isArray(raw) ? raw[0] : raw;
      setForm({
        name: d?.name ?? "",
        email: d?.email ?? "",
        phone: d?.phone ?? "",
        password: "",
        role_code: d?.role_code ?? "",
        status: d?.status ?? "active",
      });
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
      setModal((m) => ({ ...m, show: false }));
    } finally {
      setModalLoading(false);
    }
  };

  const handleFormText = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.email.trim()) return toast.error("Email is required");
    if (!form.phone.trim()) return toast.error("Mobile is required");
    if (!form.role_code) return toast.error("Role is required");
    if (!isEdit && !form.password) return toast.error("Password is required");

    const confirm = await withReactContent(Swal).fire({
      title: isEdit ? "Update Staff?" : "Save Staff?",
      text: isEdit
        ? "Do you want to update this staff member?"
        : "Do you want to add this staff member?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isEdit ? "Yes, update" : "Yes, save",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        role_code: Number(form.role_code),
        status: form.status,
        // password field exists on create only; update never sends one
        ...(form.password ? { password: form.password } : {}),
      };
      const res = isEdit
        ? await staffService.update(modal.id, payload)
        : await staffService.create(payload);
      if (res?.success) {
        toast.success(res.message);
        closeModal();
        loadStaff();
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setSaving(false);
    }
  };

  const closePwdModal = () =>
    setPwdModal({ show: false, name: "", userCode: "" });

  const openChangePassword = (member) => {
    setPwdForm(initialPasswordForm);
    setShowPwd({});
    setPwdModal({
      show: true,
      name: member?.name,
      userCode: member?.code,
    });
  };

  const handlePwdChange = (e) =>
    setPwdForm({ ...pwdForm, [e.target.name]: e.target.value });

  const handlePwdSubmit = async (e) => {
    e.preventDefault();
    if (!pwdForm.newPassword) return toast.error("New Password is required");
    if (pwdForm.newPassword !== pwdForm.confirmNewPassword)
      return toast.error("New Password and Confirm Password do not match");

    const confirm = await withReactContent(Swal).fire({
      title: "Change Password?",
      text: "Do you want to change the password?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, change it",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    try {
      setPwdSaving(true);
      const res = await staffService.changePassword({
        user_code: pwdModal.userCode,
        password: pwdForm.newPassword,
      });
      if (res?.success) {
        toast.success(res.message);
        closePwdModal();
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setPwdSaving(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleApply = (e) => {
    e?.preventDefault();
    setPageValue(1);
    setAppliedFilters({ ...filters });
  };

  const handleReset = () => {
    setFilters(initialFilters);
    setPageValue(1);
    setAppliedFilters(initialFilters);
  };

  const handleGoPage = () => {
    const n = Number(goPage);
    if (n >= 1 && n <= totalPage) {
      setPageValue(n);
    }
    setGoPage("");
  };

  const handlePagingLimit = (e) => {
    setPageValue(1);
    setPagingLimit(e.target.value);
  };

  const handlePagination = (pageValueInput) => {
    setPageValue(pageValueInput);
  };

  const staff = staffRes?.staff || [];
  const totalRecords = staffRes?.pagination?.total || 0;
  const totalPage = Math.max(1, Math.ceil(totalRecords / Number(pagingLimit)));
  const activeCount = staff.filter((v) => v?.status === "active").length;
  const fromRecord =
    totalRecords === 0 ? 0 : (pageValue - 1) * Number(pagingLimit) + 1;
  const toRecord = Math.min(pageValue * Number(pagingLimit), totalRecords);

  const roleLabel = (code) =>
    roleOptions.find((o) => o.value === code)?.label || code;

  const formatDateTime = (v) => (v ? new Date(v).toLocaleString() : "-");

  const getPageNumbers = () => {
    if (totalPage <= 5)
      return Array.from({ length: totalPage }, (_, i) => i + 1);
    const sorted = [
      ...new Set([
        1,
        2,
        totalPage - 1,
        totalPage,
        pageValue - 1,
        pageValue,
        pageValue + 1,
      ]),
    ]
      .filter((n) => n >= 1 && n <= totalPage)
      .sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push("...");
      out.push(n);
    });
    return out;
  };

  const csvHeaders = [
    { label: "Staff Code", key: "code" },
    { label: "Name", key: "name" },
    { label: "Mobile", key: "phone" },
    { label: "Email", key: "email" },
    { label: "Role Code", key: "role_code" },
    { label: "Last Login", key: "last_login" },
    { label: "Status", key: "status" },
  ];

  const pageBtn = (active, disabled) => ({
    minWidth: 32,
    height: 32,
    padding: "0 8px",
    borderRadius: 8,
    border: active ? "1px solid var(--page-active-bg)" : "1px solid #e8edf5",
    background: active ? "var(--page-active-bg)" : "#fff",
    color: active ? "var(--page-active-text)" : disabled ? "#c3cad8" : "#444",
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? "default" : "pointer",
  });

  const chip = (color, bg, border) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 10,
    background: bg,
    border: `1px solid ${border}`,
    color,
    fontSize: 13,
  });

  const filterInput = {
    borderRadius: 8,
    border: "1px solid #e8edf5",
    background: "#f8f9fe",
    fontSize: 13,
    height: 34,
  };

  const primaryBtn = {
    background: "var(--btn-bg)",
    borderRadius: 8,
    height: 34,
    fontSize: 13,
    fontWeight: 600,
  };

  const lightBtn = {
    background: "#fff",
    border: "1px solid #e8edf5",
    borderRadius: 8,
    height: 34,
    fontSize: 13,
    color: "#444",
  };

  return (
    <>
      <section className="section dashboard">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4
              className="m-0 fw-bold"
              style={{ color: "var(--page-heading)", fontSize: 20 }}
            >
              Staff
            </h4>
            <nav
              className="d-flex align-items-center gap-1 mt-1"
              style={{ fontSize: 12, color: "#475569" }}
            >
              <Link to="/dashboard" style={{ color: "#475569" }}>
                <i className="bi bi-house-door"></i>
              </Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span>Staff</span>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>
                Staff List
              </span>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              onClick={openAdd}
              className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
              style={primaryBtn}
            >
              <i className="bi bi-plus-lg"></i> Add Staff
            </button>
            <CSVLink
              data={staff}
              headers={csvHeaders}
              filename="staff.csv"
              className="btn btn-sm d-flex align-items-center gap-1 px-3"
              style={lightBtn}
            >
              <i className="bi bi-download"></i> Export
            </CSVLink>
          </div>
        </div>

        <form
          onSubmit={handleApply}
          className="card border-0 shadow-sm mb-3"
          style={{ borderRadius: 14 }}
        >
          <div className="card-body p-3">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md">
                <input
                  type="text"
                  className="form-control"
                  name="search"
                  placeholder="Search name, email, phone, code, role code"
                  value={filters.search}
                  onChange={handleFilterChange}
                  style={filterInput}
                />
              </div>
              <div className="col-6 col-md-3">
                <Select
                  options={roleOptions}
                  value={
                    roleOptions.find(
                      (o) => String(o.value) === String(filters.role_code),
                    ) || null
                  }
                  onChange={(o) =>
                    setFilters({ ...filters, role_code: o ? o.value : "" })
                  }
                  placeholder="Select Role"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={STATUS_FILTER_OPTIONS}
                  value={
                    STATUS_FILTER_OPTIONS.find(
                      (o) => o.value === filters.status,
                    ) || null
                  }
                  onChange={(o) =>
                    setFilters({ ...filters, status: o ? o.value : "" })
                  }
                  placeholder="Select Status"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-12 col-md-auto d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
                  style={primaryBtn}
                >
                  <i className="bi bi-search"></i> Apply
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="btn btn-sm d-flex align-items-center gap-1 px-3"
                  style={lightBtn}
                >
                  <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </form>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <span style={chip("#e8590c", "#fff4e6", "#ffd8a8")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{totalRecords}</b> Total Staff
          </span>
          <span style={chip("#0f8a5f", "#e6f7f1", "#b7ebd6")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{activeCount}</b> Active on this page
          </span>
          <span style={chip("#1971c2", "#e7f5ff", "#a5d8ff")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{staff.length - activeCount}</b> Inactive on this page
          </span>
        </div>

        <div
          className="card border-0 shadow-sm"
          style={{ borderRadius: 14, overflow: "hidden" }}
        >
          <div style={{ position: "relative" }}>
            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(255,255,255,0.6)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 10,
                }}
              >
                <ContentLoader loaderType="circleSpin" />
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr style={{ background: "#f8f9fe" }}>
                    {[
                      "S.No.",
                      "Staff Id",
                      "Name",
                      "Mobile",
                      "Email",
                      "Role",
                      "Last Login",
                      "Password",
                      "Status",
                      "Actions",
                    ].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          fontSize: "11px",
                          fontWeight: "700",
                          color: "#475569",
                          textTransform: "uppercase",
                          letterSpacing: "0.6px",
                          borderBottom: "2px solid #e8edf5",
                          padding: "10px 12px",
                          whiteSpace: "nowrap",
                          background: "#f8f9fe",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {staff.map((member, sno_i) => (
                    <tr
                      key={member?.id}
                      style={{ borderBottom: "1px solid #f0f2f5" }}
                    >
                      <td className="ps-3" style={{ fontSize: 13 }}>
                        {(pageValue - 1) * Number(pagingLimit) + sno_i + 1}
                      </td>
                      <td>
                        <span className="fw-medium" style={{ fontSize: 13 }}>
                          {member?.code}
                        </span>
                      </td>
                      <td>
                        <span
                          className="fw-semibold text-dark"
                          style={{ fontSize: 13 }}
                        >
                          {member?.name}
                        </span>
                      </td>
                      <td>
                        <span className="fw-medium" style={{ fontSize: 13 }}>
                          {member?.phone}
                        </span>
                      </td>
                      <td>
                        <span className="fw-medium" style={{ fontSize: 13 }}>
                          {member?.email}
                        </span>
                      </td>
                      <td>
                        <span className="fw-medium" style={{ fontSize: 13 }}>
                          {roleLabel(member?.role_code)}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: 12, color: "#6b7a90" }}>
                          {formatDateTime(member?.last_login)}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => openChangePassword(member)}
                          className="btn btn-sm d-flex align-items-center gap-1 px-2"
                          style={{
                            height: 28,
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 600,
                            background: "#fff4e6",
                            color: "#e8590c",
                            border: "none",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <i className="bi bi-key-fill"></i> Change Password
                        </button>
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={member?.status}
                          onChange={(e) =>
                            handleStatusChange(member, e.target.value)
                          }
                          style={{
                            width: "auto",
                            minWidth: 0,
                            height: 28,
                            padding: "0 26px 0 10px",
                            backgroundPosition: "right 8px center",
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 600,
                            backgroundColor:
                              member?.status === "active"
                                ? "#d4edda"
                                : "#f8d7da",
                            color:
                              member?.status === "active"
                                ? "#155724"
                                : "#721c24",
                            border: "none",
                          }}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => openExisting(member, "view")}
                            title="View"
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              background: "#e6f7f1",
                              color: "#0f8a5f",
                              border: "none",
                              padding: 0,
                            }}
                          >
                            <i
                              className={`bi ${ACTION_ICONS.view}`}
                              data-action="view"
                              style={{ fontSize: 13 }}
                            ></i>
                          </button>
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => openExisting(member, "edit")}
                            title="Edit"
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              background: "#e8f0fe",
                              color: "var(--btn-bg)",
                              border: "none",
                              padding: 0,
                            }}
                          >
                            <i
                              className={`bi ${ACTION_ICONS.edit}`}
                              data-action="edit"
                              style={{ fontSize: 13 }}
                            ></i>
                          </button>
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => handleDeleteStaff(member)}
                            title="Delete"
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              background: "#fce8e6",
                              color: "#d93025",
                              border: "none",
                              padding: 0,
                            }}
                          >
                            <i
                              className={`bi ${ACTION_ICONS.delete}`}
                              data-action="delete"
                              style={{ fontSize: 13 }}
                            ></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && staff.length === 0 && (
                    <tr>
                      <td
                        colSpan={10}
                        className="text-center text-muted py-4"
                        style={{ fontSize: 13 }}
                      >
                        No staff found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div
            className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-3"
            style={{
              borderTop: "1px solid #eef1f6",
              fontSize: 13,
              color: "#6b7a90",
            }}
          >
            <div>
              Showing{" "}
              <b>
                {fromRecord}–{toRecord}
              </b>{" "}
              of <b>{totalRecords}</b> staff
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span>Rows</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 100, borderRadius: 8 }}
                value={pagingLimit}
                onChange={handlePagingLimit}
              >
                {["10", "20", "50", "100", "500"].map((v) => (
                  <option key={v} value={v}>
                    {v}/page
                  </option>
                ))}
              </select>
              <span>Go</span>
              <input
                type="number"
                min="1"
                max={totalPage}
                className="form-control form-control-sm text-center"
                style={{ width: 60, borderRadius: 8 }}
                value={goPage}
                onChange={(e) => setGoPage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleGoPage()}
                placeholder={String(pageValue)}
              />
              <span>/ {totalPage}</span>
              <button
                type="button"
                style={pageBtn(false, false)}
                onClick={handleGoPage}
              >
                Go
              </button>
              <div className="d-flex align-items-center gap-1">
                <button
                  type="button"
                  disabled={pageValue <= 1}
                  style={pageBtn(false, pageValue <= 1)}
                  onClick={() => handlePagination(1)}
                >
                  <i className="bi bi-chevron-double-left"></i>
                </button>
                <button
                  type="button"
                  disabled={pageValue <= 1}
                  style={pageBtn(false, pageValue <= 1)}
                  onClick={() => handlePagination(pageValue - 1)}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                {getPageNumbers().map((n, i) =>
                  n === "..." ? (
                    <span key={"e" + i} className="px-1">
                      ...
                    </span>
                  ) : (
                    <button
                      type="button"
                      key={n}
                      style={pageBtn(n === pageValue, false)}
                      onClick={() => handlePagination(n)}
                    >
                      {n}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  disabled={pageValue >= totalPage}
                  style={pageBtn(false, pageValue >= totalPage)}
                  onClick={() => handlePagination(pageValue + 1)}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
                <button
                  type="button"
                  disabled={pageValue >= totalPage}
                  style={pageBtn(false, pageValue >= totalPage)}
                  onClick={() => handlePagination(totalPage)}
                >
                  <i className="bi bi-chevron-double-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <Modal
          show={modal.show}
          onHide={closeModal}
          centered
          size={isView ? "lg" : undefined}
        >
          <Modal.Header
            closeButton
            closeVariant={isView ? "white" : undefined}
            style={
              isView
                ? {
                    background:
                      "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)",
                    borderBottom: "none",
                  }
                : undefined
            }
          >
            <div>
              <Modal.Title
                className="d-flex align-items-center gap-2"
                style={
                  isView
                    ? { color: "#fff", fontSize: 18, fontWeight: 600 }
                    : { fontSize: 16, fontWeight: 700 }
                }
              >
                {isView && (
                  <i className={`bi ${ACTION_ICONS.view}`} data-action="view"></i>
                )}
                {isView
                  ? "Staff Details"
                  : isEdit
                    ? "Update Staff"
                    : "Add Staff"}
              </Modal.Title>
              {!isView && (
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(255,255,255,.78)",
                    marginTop: 2,
                  }}
                >
                  {isEdit
                    ? "Update staff information"
                    : "Add a new staff member to the system"}
                </div>
              )}
            </div>
          </Modal.Header>
          <form onSubmit={handleSubmit}>
            <Modal.Body
              style={isView ? { padding: 24, background: "#f8f9fe" } : undefined}
            >
              {modalLoading ? (
                <ContentLoader loaderType="circleSpin" />
              ) : isView ? (
                <DetailView
                  fields={[
                    { label: "Name", value: form.name },
                    { label: "Email", value: form.email },
                    { label: "Mobile", value: form.phone },
                    { label: "Role", value: roleLabel(form.role_code) },
                    { label: "Status", value: form.status, badge: true },
                  ]}
                />
              ) : (
                <div className="row g-3">
                  <div className="col-12">
                    <div style={labelStyle}>
                      Name <span className="text-danger">*</span>
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      placeholder="Enter full name"
                      value={form.name}
                      onChange={handleFormText}
                      style={inputStyle}
                    />
                  </div>
                  <div className="col-6">
                    <div style={labelStyle}>
                      Email <span className="text-danger">*</span>
                    </div>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      placeholder="Enter email"
                      value={form.email}
                      onChange={handleFormText}
                      style={inputStyle}
                    />
                  </div>
                  <div className="col-6">
                    <div style={labelStyle}>
                      Mobile <span className="text-danger">*</span>
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      name="phone"
                      placeholder="Enter mobile number"
                      value={form.phone}
                      onChange={handleFormText}
                      style={inputStyle}
                    />
                  </div>
                  <div className="col-6">
                    <div style={labelStyle}>
                      Role <span className="text-danger">*</span>
                    </div>
                    <Select
                      options={roleOptions}
                      value={
                        roleOptions.find(
                          (o) => String(o.value) === String(form.role_code),
                        ) || null
                      }
                      onChange={(o) =>
                        setForm({ ...form, role_code: o ? o.value : "" })
                      }
                      placeholder="Select Role"
                      menuPortalTarget={document.body}
                      styles={formSelectStyles}
                    />
                  </div>
                  {!isEdit && (
                    <div className="col-6">
                      <div style={labelStyle}>
                        Password <span className="text-danger">*</span>
                      </div>
                      <input
                        type="password"
                        className="form-control"
                        name="password"
                        placeholder="Enter password"
                        autoComplete="new-password"
                        value={form.password}
                        onChange={handleFormText}
                        style={inputStyle}
                      />
                    </div>
                  )}
                  <div className="col-6">
                    <div style={labelStyle}>Status</div>
                    <Select
                      options={FORM_STATUS_OPTIONS}
                      value={
                        FORM_STATUS_OPTIONS.find(
                          (o) => o.value === form.status,
                        ) || null
                      }
                      onChange={(o) =>
                        setForm({ ...form, status: o ? o.value : "active" })
                      }
                      menuPortalTarget={document.body}
                      styles={formSelectStyles}
                    />
                  </div>
                </div>
              )}
            </Modal.Body>
            {!isView && (
              <Modal.Footer className="justify-content-end gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn btn-sm px-4"
                  style={{
                    background: "#fff",
                    border: "1px solid #c3cad6",
                    borderRadius: 6,
                    height: 34,
                    fontSize: 13,
                    color: "#444",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || modalLoading}
                  className="btn btn-sm px-4 text-white fw-semibold"
                  style={{
                    background: "var(--btn-bg)",
                    border: "none",
                    borderRadius: 6,
                    height: 34,
                    fontSize: 13,
                  }}
                >
                  {isEdit ? "Update Staff" : "Add Staff"}
                </button>
              </Modal.Footer>
            )}
          </form>
        </Modal>

        <Modal show={pwdModal.show} onHide={closePwdModal} centered>
          <Modal.Header closeButton>
            <div>
              <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
                Change Password
              </Modal.Title>
              <div
                style={{
                  fontSize: 12,
                  color: "rgba(255,255,255,.78)",
                  marginTop: 2,
                }}
              >
                {pwdModal.name
                  ? `Update password for ${pwdModal.name}`
                  : "Update password"}
              </div>
            </div>
          </Modal.Header>
          <form onSubmit={handlePwdSubmit}>
            <Modal.Body>
              <div className="row g-3">
                {[
                  ["newPassword", "New Password", "Enter new password", "new-password"],
                  ["confirmNewPassword", "Confirm New Password", "Re-enter new password", "new-password"],
                ].map(([name, label, placeholder, autoComplete]) => (
                  <div className="col-12" key={name}>
                    <div style={labelStyle}>
                      {label} <span className="text-danger">*</span>
                    </div>
                    <div style={{ position: "relative" }}>
                      <input
                        type={showPwd[name] ? "text" : "password"}
                        className="form-control"
                        name={name}
                        placeholder={placeholder}
                        autoComplete={autoComplete}
                        value={pwdForm[name]}
                        onChange={handlePwdChange}
                        style={{ ...inputStyle, paddingRight: 38 }}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowPwd({ ...showPwd, [name]: !showPwd[name] })
                        }
                        title={showPwd[name] ? "Hide password" : "Show password"}
                        className="btn btn-sm d-flex align-items-center justify-content-center"
                        style={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          width: 36,
                          height: 34,
                          border: "none",
                          background: "transparent",
                          color: "#64748b",
                        }}
                      >
                        <i
                          className={`bi ${showPwd[name] ? "bi-eye-slash-fill" : "bi-eye-fill"}`}
                        ></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </Modal.Body>
            <Modal.Footer className="justify-content-end gap-2">
              <button
                type="button"
                onClick={closePwdModal}
                className="btn btn-sm px-4"
                style={{
                  background: "#fff",
                  border: "1px solid #c3cad6",
                  borderRadius: 6,
                  height: 34,
                  fontSize: 13,
                  color: "#444",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={pwdSaving}
                className="btn btn-sm px-4 text-white fw-semibold"
                style={{
                  background: "var(--btn-bg)",
                  border: "none",
                  borderRadius: 6,
                  height: 34,
                  fontSize: 13,
                }}
              >
                Change Password
              </button>
            </Modal.Footer>
          </form>
        </Modal>
      </section>
    </>
  );
};

export default StaffList;
